import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import '../../styles/Blog/Blog.css';

const BlogPost = () => {
  const { id } = useParams();
  const [blogData, setBlogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const response = await fetch('/blog-data.json');
        if (!response.ok) {
          throw new Error('Failed to fetch blog data');
        }
        const data = await response.json();
        setBlogData(data);
      } catch (err) {
        setError(err.message);
        console.error('Error loading blog data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  // Function to get image source with fallback
  const getImageSrc = (imagePath) => {
    return imagePath || '/blog-images/fallback.jpg';
  };

  if (loading) {
    return (
      <div className="blog-post-page" style={{marginTop: '80px', padding: '2rem'}}>
        <div className="container">
          <div className="loading" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div className="loading-spinner"></div>
            <h3>Loading Construction Guide</h3>
            <p>Preparing detailed professional insights...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-page" style={{marginTop: '80px', padding: '2rem'}}>
        <div className="container">
          <div className="error" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div className="error-icon">⚠️</div>
            <h3>Guide Not Available</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              Reload Guide
            </button>
          </div>
        </div>
      </div>
    );
  }

  const post = blogData?.posts?.find(p => p.id.toString() === id);

  if (!post) {
    return (
      <div className="blog-post-page" style={{marginTop: '80px', padding: '2rem'}}>
        <div className="container">
          <div className="not-found">
            <h1>Construction Guide Not Found</h1>
            <p>The professional guide you're looking for is not available.</p>
            <Link to="/blog" className="back-to-blog">
              ← Back to Construction Guides
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="blog-post-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        <motion.article 
          className="blog-post"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* Simplified Post Header */}
          <header className="post-header">
            <motion.div 
              className="post-hero"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <img 
                src={getImageSrc(post.image)} 
                alt={post.title}
                className="post-hero-image"
                onError={(e) => {
                  e.target.src = '/blog-images/fallback.jpg';
                }}
              />
              <div className="hero-overlay">
                <div className="post-meta-content">
                  <h1 className="post-title">{post.title}</h1>
                  <div className="post-meta-info">
                    <span className="post-date">{post.date}</span>
                    <span className="post-read-time">{post.readTime}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </header>

          {/* Post Content */}
          <motion.div 
            className="post-content professional-content"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Professional Summary */}
          <motion.section 
            className="key-takeaways"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h3>Key Insights</h3>
            <div className="takeaways-grid">
              <div className="takeaway-card">
                <div className="takeaway-icon">💰</div>
                <h4>Cost Analysis</h4>
                <p>Detailed breakdown of construction costs and budget planning</p>
              </div>
              <div className="takeaway-card">
                <div className="takeaway-icon">🏗️</div>
                <h4>Material Guidance</h4>
                <p>Latest material prices and quality recommendations</p>
              </div>
              <div className="takeaway-card">
                <div className="takeaway-icon">📋</div>
                <h4>Professional Insights</h4>
                <p>Expert construction techniques and best practices</p>
              </div>
            </div>
          </motion.section>
        </motion.article>

        {/* Professional Consultation CTA */}
        <motion.section 
          className="professional-consultation"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="consultation-content">
            <div className="consultation-text">
              <h3>Need Construction Guidance?</h3>
              <p>Get professional advice for your construction project in Pakistan.</p>
            </div>
            <div className="consultation-actions">
              <a href="tel:+923218836371" className="consultation-btn primary">
                <span>Call Expert</span>
                <span className="phone-number">0321 88 36 37 1</span>
              </a>
              <Link to="/contact" className="consultation-btn secondary">
                Get Consultation
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
};

export default BlogPost;